import { CircularProgress, Typography } from '@mui/material';
import { getBrandLang } from '@prismamedia/one-brandkey';
import {
  NotificationTypeEnum,
  useNotification,
} from '@prismamedia/one-components';
import { FC, useEffect, useState } from 'react';
import { Redirect, RouteComponentProps } from 'react-router-dom';
import {
  ArticleFormat,
  ArticleStatus,
  BrandKey,
  GetArticleQuery_query_article_Article,
} from '../../__generated__/queries-web';
import { useGetArticle } from '../../apollo/queries/articles.web.graphql';
import { useGetMainAuthor } from '../../apollo/queries/authors.web.graphql';
import { translateArticleQuery } from '../../apollo/queries/translateArticles.web.graphql';
import { paths } from '../../routing/Router/paths';
import { auth } from '../../utils/auth';
import { ArticleEdit } from './ArticleEdit';
import { useStyles } from './styles';
import { emptyArticle } from './utils/emptyTypes';

interface ArticleRouteProps {
  articleType: ArticleFormat;
  id: string;
  brandKey: BrandKey;
}

export const ArticleWrapper: FC<RouteComponentProps<ArticleRouteProps>> = (
  params,
) => {
  const classes = useStyles();
  const { match } = params;
  const { id } = match.params;
  const { data, loading, error, startPolling, stopPolling } = useGetArticle(id);

  useEffect(() => {
    if (
      data?.article?.format === ArticleFormat.Video &&
      data?.article?.status === ArticleStatus.Pending
    ) {
      startPolling(5000);
    }
    return () => stopPolling();
  }, [data, startPolling, stopPolling]);

  if (!id) {
    return <EmptyArticleWrapper {...params} />;
  }

  if (loading) {
    return (
      <div className={classes.centered}>
        <CircularProgress size={80} />
      </div>
    );
  }
  if (error || (data && !data.article)) {
    return (
      <Typography variant="h6">
        Une erreur est survenue, veuillez réessayer
      </Typography>
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  return <ArticleEdit data={data!.article!} />;
};

const EmptyArticleWrapper: FC<RouteComponentProps<ArticleRouteProps>> = ({
  match,
}) => {
  const { articleType, brandKey } = match.params;
  const { data: mainAuthorData, loading, error } = useGetMainAuthor(
    brandKey,
    auth.user?.id,
  );

  // User should have only 1 mainAuthor per brandKey
  const authors: any = mainAuthorData?.authors?.length
    ? [
        {
          order: 1,
          author: mainAuthorData.authors[0],
        },
      ]
    : [];

  const articleData: GetArticleQuery_query_article_Article = {
    ...emptyArticle(brandKey, articleType),
    authors,
  };

  return (
    <ArticleEdit
      data={articleData}
      loading={loading}
      error={error}
      disableConcurrentialAccess={true}
    />
  );
};

interface DuplicateArticleWrapperProps
  extends RouteComponentProps<ArticleRouteProps> {
  disableConcurrentialAccess?: boolean;
}

export const DuplicateArticleWrapper: FC<DuplicateArticleWrapperProps> = ({
  match,
  disableConcurrentialAccess = true,
}) => {
  const classes = useStyles();
  const { id, brandKey } = match.params;
  const [
    articleData,
    setArticleData,
  ] = useState<GetArticleQuery_query_article_Article | null>(null);

  const { pushNotification } = useNotification();
  const { data, loading, error } = useGetArticle(id);
  const { data: mainAuthorData, loading: authorLoading } = useGetMainAuthor(
    brandKey,
    auth.user?.id,
  );

  if (data?.article && id && brandKey && pushNotification && !articleData) {
    const { lead, body, tags, brandKey: articleBrandKey } = data.article;

    const setNonTranslatedArticle = () =>
      setArticleData({
        ...emptyArticle(brandKey, 'article'),
        lead,
        body,
        tags,
      });

    const translateArticle = async () => {
      try {
        const { data: deeplApiResult } = await translateArticleQuery(
          id,
          getBrandLang(articleBrandKey),
        );

        if (deeplApiResult.translateArticle) {
          const {
            lead: translatedLead,
            body: translatedBody,
            title: translatedTitle,
          } = deeplApiResult.translateArticle;

          setArticleData({
            ...emptyArticle(brandKey, 'article')['article'],
            lead: translatedLead,
            body: translatedBody,
            title: translatedTitle,
          });
        } else {
          setNonTranslatedArticle();
          pushNotification({
            type: NotificationTypeEnum.error,
            message: "Impossible de traduire l'article",
          });
        }
      } catch (e) {
        setNonTranslatedArticle();
        pushNotification({
          type: NotificationTypeEnum.error,
          message: "Impossible de traduire l'article",
        });
      }
    };

    if (getBrandLang(articleBrandKey) !== getBrandLang(brandKey)) {
      translateArticle();
    } else {
      setNonTranslatedArticle();
    }
  }

  if (loading || authorLoading || !auth.user || !articleData) {
    return (
      <div className={classes.centered}>
        <CircularProgress size={80} />
      </div>
    );
  }

  const { id: userId, name, avatarUrl } = auth.user;

  if (error || (data && !data.article)) {
    pushNotification({
      type: NotificationTypeEnum.error,
      message: "L'identifiant de l'article est introuvable.",
    });
    return <Redirect to={paths.ARTICLE_LIST} />;
  }

  // User should have only 1 mainAuthor per brandKey
  if (
    mainAuthorData?.authors?.length &&
    articleData &&
    !articleData?.authors.some(
      (getAuthors: any) =>
        getAuthors.author.id === mainAuthorData.authors[0].id,
    )
  ) {
    setArticleData({
      ...articleData,
      authors: [
        ...articleData.authors,
        {
          order: 1,
          __typename: 'ArticleAuthor',
          author: {
            __typename: 'Author',
            id: mainAuthorData.authors[0].id,
            name: mainAuthorData.authors[0].name,
            user: {
              __typename: 'OneUser',
              id: userId,
              name,
              avatarUrl,
            },
          },
        },
      ],
    });
  }
  return (
    <ArticleEdit
      data={articleData}
      loading={loading || authorLoading}
      disableConcurrentialAccess={disableConcurrentialAccess}
    />
  );
};
